import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PROGRAM_SECTIONS, PROGRAM_DETAILS } from "../data/content";

const Programs = () => {
  useEffect(() => { document.title = "Programs | Iron Shield Veterans Reset Center"; }, []);
  return (
    <>
      <PageHero
        eyebrow="Programs"
        lines={["A complete model —", "housing to reintegration."]}
        intro="Iron Shield isn't a single service. It's a connected path: a safe place to stay, structured support to stabilize, and a plan that leads back to independent, stable life."
      />

      <div className="container-x py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Side nav */}
        <aside className="lg:col-span-3">
          <div className="lg:sticky lg:top-28">
            <div className="label-caps text-navy mb-5">On this page</div>
            <nav className="flex flex-col gap-1 border-l border-softgray" data-testid="programs-sidenav">
              {PROGRAM_SECTIONS.map((s) => (
                <a key={s.id} href={`#${s.id}`} className="group flex items-center gap-3 py-2.5 pl-4 -ml-px border-l-2 border-transparent hover:border-gold transition-colors" data-testid={`programs-nav-${s.id}`}>
                  <span className="font-heading text-xs font-black text-gold">{s.num}</span>
                  <span className="text-sm font-medium text-charcoal/70 group-hover:text-navy transition-colors">{s.title}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Sections */}
        <div className="lg:col-span-9 space-y-24">
          {PROGRAM_SECTIONS.map((s, i) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`program-section-${s.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                <div className="md:col-span-7">
                  <Reveal>
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-heading text-2xl font-black text-gold">{s.num}</span>
                      <span className="h-px flex-1 bg-softgray" />
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-black text-navy leading-[1.1]">{s.title}</h2>
                    <p className="mt-5 text-charcoal/80 md:text-lg leading-relaxed">{s.body}</p>
                    <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {s.points.map((p) => (
                        <li key={p} className="flex items-start gap-3 text-charcoal/80">
                          <Check className="h-5 w-5 text-teal shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="text-sm md:text-base">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                </div>
                <div className="md:col-span-5">
                  <Reveal delay={0.1}>
                    <div className="relative aspect-[4/3] overflow-hidden border border-softgray">
                      <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
                    </div>
                  </Reveal>
                </div>
              </div>
            </section>
          ))}

          {/* Eligibility + length of stay */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="programs-eligibility">
            <Reveal className="border border-softgray bg-white p-8">
              <div className="label-caps text-navy mb-4">Eligibility snapshot</div>
              <ul className="space-y-3 text-charcoal/80">
                {["U.S. military veteran status", "Experiencing or at risk of homelessness", "Willing to engage in a stabilization plan", "Located in or able to relocate to our service area"].map((e) => (
                  <li key={e} className="flex items-start gap-3"><Check className="h-5 w-5 text-teal shrink-0 mt-0.5" /> <span>{e}</span></li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1} className="border border-softgray bg-white p-8">
              <div className="label-caps text-navy mb-4">Length of stay</div>
              <p className="text-charcoal/80 leading-relaxed">Iron Shield is transitional by design. Most veterans stay for several months while a permanent housing path is built — long enough to stabilize, short enough to keep momentum toward independence.</p>
            </Reveal>
          </section>
        </div>
      </div>

      {/* Explore each program in depth */}
      <section className="bg-offwhite border-t border-softgray py-16 md:py-20" data-testid="programs-explore">
        <div className="container-x">
          <div className="label-caps text-navy mb-3">Explore in depth</div>
          <h2 className="font-heading text-3xl md:text-4xl font-black text-navy">Each program, in full detail.</h2>
          <p className="mt-3 text-charcoal/70 max-w-2xl">Open any program for its overview, key services, eligibility snapshot, and length-of-stay timeline.</p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PROGRAM_DETAILS.map((p) => (
              <Link key={p.id} to={`/programs/${p.id}`} data-testid={`program-detail-link-${p.id}`} className="i-card p-6 group hover:border-gold transition-colors flex flex-col">
                <div className="label-caps text-gold">{p.category}</div>
                <h3 className="mt-2 font-heading text-lg font-bold text-navy leading-snug">{p.title}</h3>
                <p className="mt-2 text-sm text-slate leading-relaxed flex-1">{p.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 label-caps text-navy group-hover:text-gold-deep transition-colors">View details <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA footer */}
      <section className="bg-navy text-offwhite py-16 md:py-20" data-testid="programs-cta">
        <div className="container-x flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h3 className="font-heading text-3xl md:text-4xl font-black">Ready to start?</h3>
            <p className="mt-3 text-offwhite/75 max-w-xl">Begin intake as a veteran, or refer someone you're supporting. We respond quickly.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admissions" data-testid="programs-cta-intake" className="group inline-flex items-center gap-2 bg-offwhite text-navy px-7 py-4 rounded-sm text-sm font-semibold hover:bg-white transition-colors">
              Start intake <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/partners" data-testid="programs-cta-refer" className="inline-flex items-center gap-2 border border-offwhite/40 px-7 py-4 rounded-sm text-sm font-semibold hover:bg-offwhite hover:text-navy transition-colors">
              Refer a Veteran
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Programs;
